import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  useTheme,
  InputAdornment,
  IconButton,
  CircularProgress,
  Typography,
  Button,
  Fade,
  Link,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Visibility from "@mui/icons-material/VisibilityRounded";
import VisibilityOff from "@mui/icons-material/VisibilityOffRounded";
import {
  doSendSignInLinkToEmail,
  doSignInWithEmailAndPassword,
  doSendPasswordResetEmail,
} from "../../firebase/auth";
import { getAuth, signOut } from "firebase/auth";
import { useAuth } from "../../contexts/AuthContext";
import { Navigate } from "react-router-dom";
import api from "services/api";
import { useStateContext } from "contexts/ContextProvider";
import dayjs from "dayjs";
import axios from "axios";
import { usePageTitle } from "hooks/usePageTitle";

const DISABLED_ACCOUNT_MESSAGE =
  "Your account has been disabled by an admin, please reach out to your manager for further details";

const Login = () => {
  usePageTitle("Login");

  const { userLoggedIn } = useAuth();
  const { showSnackbar, openDialog, closeDialog } = useStateContext();

  const theme = useTheme();
  const [email, setEmail] = useState(
    window.localStorage.getItem("Email") || "",
  );
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // State for With Password switch. This controls password field visibility.
  const [withPassword] = useState(true);
  // State for controlling the fade-in effect for the page content
  const [fadeIn, setFadeIn] = useState(false);
  // State for storing user's IP address
  const [userIP, setUserIP] = useState("");

  // Regular expression for simple email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(email);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && isValidEmail && !loading) {
      if (withPassword) {
        // Trigger only if password is provided
        if (password) handleLoginWithPass();
      } else {
        handleLogin();
      }
    }
  };

  const [message, setMessage] = useState("");
  const [color, setColor] = useState(theme.palette.primary.main);

  // Get user's IP address using multiple fallback services
  const getUserIP = async () => {
    // Create isolated axios instance for external calls
    const externalAxios = axios.create({ timeout: 5000 });

    try {
      // Try ipify API first
      const response = await externalAxios.get(
        "https://api.ipify.org?format=json",
      );
      setUserIP(response.data.ip);
      return response.data.ip;
    } catch (error) {
      try {
        // Fallback to jsonip.com
        const response = await externalAxios.get("https://jsonip.com");
        setUserIP(response.data.ip);
        return response.data.ip;
      } catch (error) {
        try {
          // Final fallback to ipapi.co
          const response = await externalAxios.get("https://ipapi.co/json/");
          setUserIP(response.data.ip);
          return response.data.ip;
        } catch (error) {
          // Silently fail - IP is optional
          return "";
        }
      }
    }
  };

  // Trigger the fade-in effect on mount and get user IP
  useEffect(() => {
    const timer = setTimeout(() => {
      setFadeIn(true);
    }, 100);

    // Get user IP address (optional, don't throw on failure)
    getUserIP().catch(() => {});

    return () => clearTimeout(timer);
  }, []);

  const addLoginHistory = async (success, error = null) => {
    try {
      // Use axios directly to avoid authentication requirements
      await axios.post(
        `${process.env.REACT_APP_BASE_URL || "http://localhost:5001"}/login/addLoginHistory`,
        {
          data: {
            Email: email,
            LoginAt: dayjs().format(),
            Success: success,
            IPAddress: userIP,
            UserAgent: window.navigator.userAgent,
            ErrorCode: error,
          },
        },
      );
    } catch (err) {
      console.error("Error recording login history:", err.message);
      console.error("Full error:", err.response?.data || err);
    }
  };

  //------------------------- FIREBASE STUFF ---------------------------------

  const handleLoginWithPass = async () => {
    setLoading(true);

    try {
      await doSignInWithEmailAndPassword(email.trim(), password);

      const memberResponse = await api.get("/members/me");
      const member = memberResponse.data;

      window.localStorage.setItem("Email", member?.Email || email.trim());
      addLoginHistory(true);
    } catch (error) {
      const status = error?.response?.status;
      const accountDisabled = error?.response?.data?.accountDisabled;
      const errorCode = error?.code;

      if (status === 403 && accountDisabled) {
        addLoginHistory(false, "Account Disabled");
        try {
          await signOut(getAuth());
        } catch (_) {
          // best-effort sign out only
        }
        showSnackbar(
          DISABLED_ACCOUNT_MESSAGE,
          theme,
          "error",
          theme.palette.background.alt,
        );
        setLoading(false);
        return;
      }

      let message = "Sorry, email or password is incorrect. Please retry.";

      if (errorCode === "auth/user-disabled") {
        message = DISABLED_ACCOUNT_MESSAGE;
      }

      if (errorCode === "auth/too-many-requests") {
        message = "Too many requests, please retry after 3 minutes.";
      }

      if (status === 404 && error?.response?.data?.error === "Member not found") {
        message = "Account is authenticated but not provisioned in the portal. Please contact Admin.";
      }

      addLoginHistory(false, errorCode || error?.response?.data?.error || "Login Failed");
      showSnackbar(message, theme, "error", theme.palette.background.alt);
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setLoading(true);

    let member = null;

    api
      .get(`/members/email/${email.toLowerCase()}`)
      .then(async (response) => {
        member = response.data;

        if (!member) {
          setTimeout(() => {
            showSnackbar(
              "Sorry, email is wrong, please check you typed your email correctly, or contact the Admin.",
              theme,
              "error",
              theme.palette.background.alt,
            );
            setLoading(false);
          }, 1000);

          return;
        }

        await doSendSignInLinkToEmail(email)
          .then(() => {
            setColor(theme.palette.primary.main);

            setTimeout(() => {
              setMessage(
                `Welcome back, ${member.Name}!\nWe have sent you a confirmation email, please use it to login.`,
              );
              window.localStorage.setItem("Email", email);
            }, 1000);
          })
          .catch((error) => {
            const errorMessage = error.message;
            showSnackbar(
              errorMessage,
              theme,
              "error",
              theme.palette.background.alt,
            );
            setLoading(false);
          });
      })
      .catch((error) => {
        console.error("Error fetching member data:", error);

        if (error.response?.status === 403 && error.response?.data?.accountDisabled) {
          showSnackbar(
            DISABLED_ACCOUNT_MESSAGE,
            theme,
            "error",
            theme.palette.background.alt,
          );
          setLoading(false);
          return;
        }

        setTimeout(() => {
          showSnackbar(
            "Sorry, email is wrong, please check you typed your email correctly, or contact the Admin.",
            theme,
            "error",
            theme.palette.background.alt,
          );
          setLoading(false);
        }, 1000);
      });
  };

  if (userLoggedIn) return <Navigate to={"/dashboard"} replace={true} />;

  // Email form without the global Switch
  const getEmailForm = () => {
    return (
      <Box
        width="100%"
        height="auto"
        display="flex"
        flexDirection="column"
        sx={{
          px: { xs: 2, sm: 4, md: 8, xl: 4 },
        }}
      >
        {/* Header container */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: { xs: 3, sm: 4, md: 5 },
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
              color: theme.palette.primary.main,
              textAlign: "center",
            }}
          >
            CE Portal
          </Typography>
        </Box>
        {/* Input container */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: "100%",
            mb: { xs: 3, sm: 4, md: 5 },
            //gap: { xs: 1, sm: 1.5, md: 2 },
          }}
        >
          {/* Email TextField */}
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            InputProps={{
              endAdornment: loading && (
                <InputAdornment
                  position="end"
                  sx={{
                    mr: 1,
                    color: theme.palette.primary.main,
                  }}
                >
                  <CircularProgress size={20} color="inherit" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: email
                    ? isValidEmail
                      ? theme.palette.primary.main
                      : theme.palette.error.main
                    : theme.palette.text.primary,
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "1.4rem",
              },
              "& .MuiInputLabel-root": {
                fontSize: "1.1rem",
              },
              "label + &": {
                marginTop: theme.spacing(6),
              },
            }}
          />
          {/* Conditionally render Password TextField if withPassword is true */}
          {withPassword && (
            <TextField
              label="Password"
              variant="outlined"
              fullWidth
              margin="normal"
              size="large"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type={showPassword ? "text" : "password"}
              disabled={loading}
              onKeyDown={handleKeyDown}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                fontSize: { xs: "1rem", sm: "1.2rem", md: "1.4rem" },
                "& .MuiOutlinedInput-root": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: { xs: "1rem", sm: "1.2rem", md: "1.4rem" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                },
                "label + &": {
                  marginTop: theme.spacing(2),
                },
              }}
            />
          )}

          {/* Forgot Password Link - Only show when withPassword is true */}
          {withPassword && (
            <Box sx={{ textAlign: "right" }}>
              <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  let resetEmail = email; // Use current email as default
                  openDialog({
                    theme: theme,
                    title: "Reset Password",
                    customContent: (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          Enter your email address and we'll send you a link to
                          reset your password.
                        </Typography>
                        <TextField
                          autoFocus
                          fullWidth
                          size="small"
                          label="Email"
                          type="email"
                          variant="outlined"
                          defaultValue={resetEmail}
                          onChange={(e) => {
                            resetEmail = e.target.value;
                          }}
                        />
                      </Box>
                    ),
                    primaryAction: "Send Reset Link",
                    secondaryAction: "Cancel",
                    primaryOnClick: async () => {
                      // Validate email before sending
                      if (!resetEmail || !emailRegex.test(resetEmail)) {
                        showSnackbar(
                          "Please enter a valid email address",
                          theme,
                          "error",
                          theme.palette.background.alt,
                        );
                        return;
                      }

                      try {
                        // First, check if the email belongs to a member
                        const memberResponse = await api.get(
                          `/members/email/${resetEmail.toLowerCase()}`,
                        );

                        if (!memberResponse.data) {
                          showSnackbar(
                            "This email does not belong to a member. Please check your email address or contact the administrator.",
                            theme,
                            "error",
                            theme.palette.background.alt,
                          );
                          return; // Don't close dialog, let user try again
                        }

                        await doSendPasswordResetEmail(resetEmail);

                        showSnackbar(
                          "Password reset email sent. Check your inbox and spam folder.",
                          theme,
                          "success",
                          theme.palette.background.alt,
                        );
                        closeDialog();
                      } catch (error) {
                        let friendlyMessage = "Failed to send reset email";

                        // Check if this is a member lookup error
                        if (error.response?.status === 404) {
                          friendlyMessage =
                            "This email does not belong to a member. Please check your email address or contact the administrator.";
                        }
                        // Provide specific error messages based on Firebase error codes
                        else if (error.code === "auth/user-not-found") {
                          friendlyMessage =
                            "No account found with this email address";
                        } else if (error.code === "auth/invalid-email") {
                          friendlyMessage = "Invalid email address";
                        } else if (error.code === "auth/too-many-requests") {
                          friendlyMessage =
                            "Too many reset requests. Please try again later.";
                        }

                        showSnackbar(
                          friendlyMessage,
                          theme,
                          "error",
                          theme.palette.background.alt,
                        );
                        // Don't close dialog on error so user can try again
                      }
                    },
                    secondaryOnClick: closeDialog,
                  });
                }}
                disabled={loading}
                sx={{
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.5 : 1,
                  fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                }}
              >
                Forgot Password?
              </Link>
            </Box>
          )}

          {/* Log In Button - calls proper handler based on withPassword switch */}
          <Box
            sx={{
              mt: { xs: 2, sm: 3, md: 4 },
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={withPassword ? handleLoginWithPass : handleLogin}
              disabled={
                loading || !isValidEmail || (withPassword ? !password : false)
              }
              sx={{
                borderRadius: "12px",
                backgroundColor: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.buttonHover,
                },
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 1, sm: 1.25, md: 1.5 },
                fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                fontWeight: "bold",
              }}
            >
              Log In
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  const getTextMessage = () => {
    return (
      <Box
        width={{ xs: "100%", sm: "400px", md: "500px" }}
        height="auto"
        minHeight={{ xs: "200px", sm: "225px", md: "250px" }}
        display="flex"
        sx={{
          backgroundColor: theme.palette.background.alt,
          borderRadius: "1.5rem",
          p: { xs: 2, sm: 2.5, md: 3 },
          boxShadow: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            align="center"
            color={color}
            sx={{ whiteSpace: "pre-line" }}
          >
            {message}
          </Typography>
        </Box>
      </Box>
    );
  };

  const getLoading = () => {
    return (
      <Box
        width={{ xs: "100%", sm: "400px", md: "500px" }}
        height="auto"
        minHeight={{ xs: "200px", sm: "225px", md: "250px" }}
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          backgroundColor: theme.palette.background.alt,
          borderRadius: "1.5rem",
          p: { xs: 2, sm: 2.5, md: 3 },
          boxShadow: 3,
        }}
      >
        <CircularProgress
          size="5rem"
          sx={{
            color: theme.palette.primary.main,
          }}
        />
      </Box>
    );
  };

  const getForm = () => {
    if (!loading) return getEmailForm();
    else if (message === "") return getLoading();
    else return getTextMessage();
  };

  return (
    <Fade in={fadeIn} timeout={1000}>
      <Box
        display="flex"
        flexDirection="column"
        minHeight="100vh"
        sx={{
          backgroundColor: theme.palette.background.alt,
          position: "relative",
        }}
      >
        {/* Logo - centered on mobile, left on larger screens */}
        <Box
          sx={{
            position: "absolute",
            top: { xs: 16, sm: 24, md: 32 },
            left: { xs: "50%", sm: 24, md: 32 },
            transform: { xs: "translateX(-50%)", sm: "none" },
            width: { xs: "180px", sm: "240px", md: "360px" },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
          }}
        >
          <img
            src="/assets/ce-logo-full-h.svg"
            alt="CE Portal Logo"
            style={{ maxWidth: "100%", maxHeight: "100%" }}
          />
        </Box>

        {/* Main content using Grid */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            px: { xs: 2, sm: 3 },
          }}
        >
          <Grid
            container
            sx={{
              width: "100%",
              maxWidth: "1200px",
              minHeight: { xs: "auto", sm: "50vh" },
              backgroundColor: theme.palette.background.default,
              borderRadius: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
              boxShadow: 3,
              overflow: "hidden",
            }}
          >
            {/* Left decorative section - Only visible on xl */}
            <Grid
              size={{ xs: 0, xl: 6 }}
              sx={{
                display: { xs: "none", xl: "flex" },
                pt: { xl: 4 },
                pb: { xl: 8 },
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
                minHeight: { xl: "300px" },
                borderRight: { xl: `1px solid ${theme.palette.divider}` },
              }}
            >
              {/* Background image container */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: 0.1,
                  scale: 0.75,
                  backgroundImage: 'url("/assets/ce-logo.svg")',
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  zIndex: 1,
                  animation: `zoomInOut 12s ease-in-out infinite`,
                  "@keyframes zoomInOut": {
                    "0%": {
                      transform: "scale(1)",
                    },
                    "50%": {
                      transform: "scale(0.85)",
                    },
                    "100%": {
                      transform: "scale(1)",
                    },
                  },
                }}
              />
            </Grid>

            {/* Right form section */}
            <Grid
              size={{ xs: 12, xl: 6 }}
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {getForm()}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Fade>
  );
};

export default Login;
